import _slicedToArray from "/vercel/path0/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import * as React from 'react';
import * as THREE from 'three';
import { useThree, useFrame } from '@react-three/fiber';

var isOrthographic = function isOrthographic(def) {
  return def && def.isOrthographicCamera;
};

var isBox3 = function isBox3(def) {
  return def && def.isBox3;
};

var context = /*#__PURE__*/React.createContext(null);

function Bounds(_ref) {
  var children = _ref.children,
      _ref$damping = _ref.damping,
      damping = _ref$damping === void 0 ? 6 : _ref$damping,
      fit = _ref.fit,
      clip = _ref.clip,
      observe = _ref.observe,
      _ref$margin = _ref.margin,
      margin = _ref$margin === void 0 ? 1.2 : _ref$margin,
      _ref$eps = _ref.eps,
      eps = _ref$eps === void 0 ? 0.01 : _ref$eps,
      onFit = _ref.onFit;
  var ref = React.useRef(null);

  var _useThree = useThree(),
      camera = _useThree.camera,
      invalidate = _useThree.invalidate,
      size = _useThree.size,
      controlsImpl = _useThree.controls;

  var controls = controlsImpl;
  var onFitRef = React.useRef(onFit);
  onFitRef.current = onFit;

  function equals(a, b) {
    return Math.abs(a.x - b.x) < eps && Math.abs(a.y - b.y) < eps && Math.abs(a.z - b.z) < eps;
  }

  function damp(v, t, lambda, delta) {
    v.x = THREE.MathUtils.damp(v.x, t.x, lambda, delta);
    v.y = THREE.MathUtils.damp(v.y, t.y, lambda, delta);
    v.z = THREE.MathUtils.damp(v.z, t.z, lambda, delta);
  }

  var _React$useState = React.useState(function () {
    return {
      animating: false,
      focus: new THREE.Vector3(),
      camera: new THREE.Vector3(),
      zoom: 1
    };
  }),
      _React$useState2 = _slicedToArray(_React$useState, 1),
      current = _React$useState2[0];

  var _React$useState3 = React.useState(function () {
    return {
      focus: new THREE.Vector3(),
      camera: new THREE.Vector3(),
      zoom: 1
    };
  }),
      _React$useState4 = _slicedToArray(_React$useState3, 1),
      goal = _React$useState4[0];

  var _React$useState5 = React.useState(function () {
    return new THREE.Box3();
  }),
      _React$useState6 = _slicedToArray(_React$useState5, 1),
      box = _React$useState6[0];

  var api = React.useMemo(function () {
    function getSize() {
      var size = box.getSize(new THREE.Vector3());
      var center = box.getCenter(new THREE.Vector3());
      var maxSize = Math.max(size.x, size.y, size.z);
      var fitHeightDistance = isOrthographic(camera) ? maxSize * 4 : maxSize / (2 * Math.atan(Math.PI * camera.fov / 360));
      var fitWidthDistance = isOrthographic(camera) ? maxSize * 4 : fitHeightDistance / camera.aspect;
      var distance = margin * Math.max(fitHeightDistance, fitWidthDistance);
      return {
        box: box,
        size: size,
        center: center,
        distance: distance
      };
    }

    return {
      getSize: getSize,
      refresh: function refresh(object) {
        if (isBox3(object)) box.copy(object);else {
          var target = object || ref.current;
          target.updateWorldMatrix(true, true);
          box.setFromObject(target);
        }

        if (box.isEmpty()) {
          var max = camera.position.length() || 10;
          box.setFromCenterAndSize(new THREE.Vector3(), new THREE.Vector3(max, max, max));
        }

        if ((controls == null ? void 0 : controls.constructor.name) === 'OrthographicTrackballControls') {
          // Put camera on a sphere along which it should moves
          var _getSize = getSize(),
              distance = _getSize.distance;

          var direction = camera.position.clone().sub(controls.target).normalize().multiplyScalar(distance);
          var newPos = controls.target.clone().add(direction);
          camera.position.copy(newPos);
        }

        return this;
      },
      clip: function clip() {
        var _getSize2 = getSize(),
            distance = _getSize2.distance;

        if (controls) controls.maxDistance = distance * 10;
        camera.near = distance / 100;
        camera.far = distance * 100;
        camera.updateProjectionMatrix();
        if (controls) controls.update();
        invalidate();
        return this;
      },
      fit: function fit() {
        current.camera.copy(camera.position);
        if (controls) current.focus.copy(controls.target);

        var _getSize3 = getSize(),
            center = _getSize3.center,
            distance = _getSize3.distance;

        var direction = center.clone().sub(camera.position).normalize().multiplyScalar(distance);
        goal.camera.copy(center).sub(direction);
        goal.focus.copy(center);

        if (isOrthographic(camera)) {
          current.zoom = camera.zoom;
          var maxHeight = 0,
              maxWidth = 0;
          var vertices = [new THREE.Vector3(box.min.x, box.min.y, box.min.z), new THREE.Vector3(box.min.x, box.max.y, box.min.z), new THREE.Vector3(box.min.x, box.min.y, box.max.z), new THREE.Vector3(box.min.x, box.max.y, box.max.z), new THREE.Vector3(box.max.x, box.max.y, box.max.z), new THREE.Vector3(box.max.x, box.max.y, box.min.z), new THREE.Vector3(box.max.x, box.min.y, box.max.z), new THREE.Vector3(box.max.x, box.min.y, box.min.z)]; // Transform the center and each corner to camera space

          center.applyMatrix4(camera.matrixWorldInverse);

          for (var _i = 0, _vertices = vertices; _i < _vertices.length; _i++) {
            var v = _vertices[_i];
            v.applyMatrix4(camera.matrixWorldInverse);
            maxHeight = Math.max(maxHeight, Math.abs(v.y - center.y));
            maxWidth = Math.max(maxWidth, Math.abs(v.x - center.x));
          }

          maxHeight *= 2;
          maxWidth *= 2;
          var zoomForHeight = (camera.top - camera.bottom) / maxHeight;
          var zoomForWidth = (camera.right - camera.left) / maxWidth;
          goal.zoom = Math.min(zoomForHeight, zoomForWidth) / margin;

          if (!damping) {
            camera.zoom = goal.zoom;
            camera.updateProjectionMatrix();
          }
        }

        if (damping) {
          current.animating = true;
        } else {
          camera.position.copy(goal.camera);
          camera.lookAt(goal.focus);

          if (controls) {
            controls.target.copy(goal.focus);
            controls.update();
          }
        }

        if (onFitRef.current) onFitRef.current(this.getSize());
        invalidate();
        return this;
      }
    };
  }, [box, camera, controls, margin, damping, invalidate]);
  React.useLayoutEffect(function () {
    if (controls) {
      // Try to prevent drag hijacking
      var callback = function callback() {
        return current.animating = false;
      };

      controls.addEventListener('start', callback);
      return function () {
        return controls.removeEventListener('start', callback);
      };
    }
  }, [controls]); // Scale pointer on window resize

  var count = React.useRef(0);
  React.useLayoutEffect(function () {
    if (observe || count.current++ === 0) {
      api.refresh();
      if (fit) api.fit();
      if (clip) api.clip();
    }
  }, [size, clip, fit, observe, camera, controls]);
  useFrame(function (state, delta) {
    if (current.animating) {
      damp(current.focus, goal.focus, damping, delta);
      damp(current.camera, goal.camera, damping, delta);
      current.zoom = THREE.MathUtils.damp(current.zoom, goal.zoom, damping, delta);
      camera.position.copy(current.camera);

      if (isOrthographic(camera)) {
        camera.zoom = current.zoom;
        camera.updateProjectionMatrix();
      }

      if (!controls) {
        camera.lookAt(current.focus);
      } else {
        controls.target.copy(current.focus);
        controls.update();
      }

      invalidate();
      if (isOrthographic(camera) && !(Math.abs(current.zoom - goal.zoom) < eps)) return;
      if (!isOrthographic(camera) && !equals(current.camera, goal.camera)) return;
      if (controls && !equals(current.focus, goal.focus)) return;
      current.animating = false;
    }
  });
  return /*#__PURE__*/React.createElement("group", {
    ref: ref
  }, /*#__PURE__*/React.createElement(context.Provider, {
    value: api
  }, children));
}

function useBounds() {
  return React.useContext(context);
}

export { Bounds, useBounds };